import React, { useEffect, useRef, useState } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import Swiper from "swiper/bundle";
import { Swiper as SwiperReact, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css/bundle";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  ShieldCheck,
  Clock3,
  Wallet,
  HeartHandshake,
  ClipboardList,
  CreditCard,
  Smartphone,
  CalendarCheck,
  ChevronRight,
} from "lucide-react";

const benefits = [
  {
    icon: ShieldCheck,
    title: "Trusted",
    subtitle: "Provider Network",
  },
  {
    icon: Clock3,
    title: "Convenient Care",
    subtitle: "Anytime, Anywhere",
  },
  {
    icon: Wallet,
    title: "Affordable",
    subtitle: "Health Plans",
  },
  {
    icon: HeartHandshake,
    title: "For Individuals",
    subtitle: "& Businesses",
  },
];

// Trust points under the hero buttons (sits on the hero's dark-green background)
function HealthcareBenefits() {
  return (
    <ul className="list-none pl-[0px] mt-8 mb-[0px] pt-7 max-w-[600px] grid grid-cols-2 min-[768px]:grid-cols-4 gap-x-[16px] gap-y-[20px] border-t-[1px] border-solid border-white/[0.14]">
      {benefits.map(({ icon: Icon, title, subtitle }) => (
        <li key={title} className="flex min-w-0 items-center gap-[12px] min-[768px]:flex-col min-[768px]:items-start min-[768px]:gap-[10px]">
          <span className="flex items-center justify-center shrink-0 w-[40px] h-[40px] rounded-[12px] border-[1px] border-solid border-white/[0.12] bg-white/[0.06] text-[#d6ea4f]">
            <Icon aria-hidden="true" className="w-[20px] h-[20px]" strokeWidth={1.8} />
          </span>
          <span className="min-w-0 flex flex-col">
            <span className="text-[14px] font-bold leading-[1.3] text-white">{title}</span>
            <span className="text-[12px] min-[576px]:text-[13px] leading-[1.35] text-white/65">{subtitle}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}


const services = [
  {
    title: "Tele Consultation",
    desc: "Reach a doctor by phone or app, no clinic visit needed.",
    img: "assets/img/tele-consultation.jpg",
  },
  {
    title: "Diagnostics",
    desc: "Lab tests and health checkups for an accurate diagnosis.",
    img: "assets/img/diagnostics.jpg",
  },
  {
    title: "Consultation",
    desc: "Book in-person visits for a full physical examination.",
    img: "assets/img/consultation.jpg",
  },
  {
    title: "Mental Wellness",
    desc: "Support to manage stress and anxiety, and feel steady again.",
    img: "assets/img/mental-wellness.jpg",
  },
  {
    title: "Vitals via Face Scan",
    desc: "Read key vitals in seconds with an AI-powered face scan.",
    img: "assets/img/face-scans.jpg",
  },
  {
    title: "Chat With Experts",
    desc: "Message a healthcare professional and get advice in real time.",
    img: "assets/img/chat-with-expert.jpg",
  },
  {
    title: "Video Consultation",
    desc: "See a doctor face to face over a secure video call, anywhere.",
    img: "assets/img/video-consultation.jpg",
  },
  {
    title: "Naturopathy",
    desc: "Natural, whole-body treatments and lifestyle correction.",
    img: "assets/img/naturopathy.jpg",
  },
];

const Services = () => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  return (
    <section
      className="relative scroll-mt-24 overflow-hidden bg-[#0b2a23] bg-hero-card bg-hero-grid py-[64px] min-[992px]:py-[100px]"
      id="services"
    >
      <div className="max-w-[1240px] mx-auto px-[18px] min-[576px]:px-8">
        {/* Section Header */}
        <div className="max-w-[640px]">
          <Eyebrow tone="dark">Medical services</Eyebrow>
          <h2 className="mt-[20px] mb-[0px] text-[34px] sm:text-[44px] xl:text-[48px] font-extrabold leading-[1.08] tracking-[-0.03em] text-white">
            Explore our wide range <br className="hidden sm:inline" />
            of <Highlight tone="dark">medical services</Highlight>
          </h2>
          <p className="mt-[20px] mb-[0px] max-w-[480px] text-[16px] sm:text-[18px] leading-[1.6] text-white/75">
            A comprehensive suite of services to support your well-being every
            day.
          </p>
        </div>

        {/* Slider */}
        <div className="services-slider relative mt-12">
          <SwiperReact
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={24}
            slidesPerView={1}
            loop={true}
            loopAdditionalSlides={3}
            autoplay={{
              delay: 3800,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            speed={650}
            pagination={{ clickable: true, el: ".services-pagination" }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }}
            navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
            breakpoints={{
              640: { slidesPerView: 3, spaceBetween: 20 },
              1024: { slidesPerView: 4, spaceBetween: 24 },
            }}
          >
            {services.map((s, i) => (
              <SwiperSlide key={i} className=" ">
                <a
                  href={s.href}
                  className="group relative mx-auto block h-[280px] w-full max-w-[300px] overflow-hidden rounded-[28px] sm:h-[320px] lg:h-[380px]"
                >
                  <img
                    src={s.img}
                    alt={s.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[300ms] ease-out group-hover:scale-[1.05]"
                  />

                  {/* Rich layered gradient scrim */}
                  <div
                    className="pointer-events-none absolute inset-0 transition-opacity duration-500"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(9,12,8,0.92) 0%, rgba(9,12,8,0.72) 18%, rgba(9,12,8,0.30) 42%, rgba(9,12,8,0) 66%)",
                    }}
                  />
                  {/* Subtle warm brand tint hugging the very bottom */}
                  <div
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 opacity-70 mix-blend-soft-light"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(31,138,79,0.55), rgba(31,138,79,0))",
                    }}
                  />

                  {/* Text over gradient */}
                  <div className="absolute inset-x-0 bottom-0 p-6 transition-transform duration-500 group-hover:-translate-y-1">
                    <h3 className="mb-[0px] text-[22px] font-extrabold leading-[1.2] tracking-[-0.02em] text-white">
                      {s.title}
                    </h3>
                    <p className="mt-[8px] mb-[0px] text-[14px] leading-[1.45] text-white/80">
                      {s.desc}
                    </p>
                  </div>
                </a>
              </SwiperSlide>
            ))}
          </SwiperReact>

          {/* Controls: arrows + pagination */}
          {/* <div className=" flex items-center justify-center gap-4">
            <button
              ref={prevRef}
              aria-label="Previous services"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#d6ddd0] bg-white text-[#3f5133] transition-colors hover:border-[#1AAD9D] hover:text-[#1AAD9D]"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="services-pagination flex items-center justify-center gap-2" />

            <button
              ref={nextRef}
              aria-label="Next services"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#d6ddd0] bg-white text-[#3f5133] transition-colors hover:border-[#1AAD9D] hover:text-[#1AAD9D]"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div> */}
        </div>
      </div>

      {/* Pagination styling (scoped) */}
      <style>{`
        .services-pagination .swiper-pagination-bullet {
          width: 8px; height: 8px;
          background: rgba(255,255,255,0.3); opacity: 1;
          border-radius: 9999px;
          transition: all .25s ease;
        }
        .services-pagination .swiper-pagination-bullet-active {
          width: 26px; background: #d6ea4f;
        }

        @media (prefers-reduced-motion: reduce) {
          .services-slider .group img { transition: none !important; }
        }
      `}</style>
    </section>
  );
};

// tone="dark" is for sections on the hero's dark-green background
const eyebrowClass =
  "inline-flex items-center gap-2.5 py-1.5 pl-3 pr-3.5 border-[1px] border-solid rounded-full text-[13px] font-medium";

export function Eyebrow({ children, tone = "light" }) {
  const dark = tone === "dark";
  return (
    <span className={`${eyebrowClass} ${dark ? "border-white/[0.28] text-white" : "border-[#0d2f27]/[0.18] text-[#0d2f27]"}`}>
      <span className={`w-[7px] h-[7px] shrink-0 rounded-full ${dark ? "bg-[#d6ea4f]" : "bg-[#1f8a4f]"}`} />
      {children}
    </span>
  );
}

export function Highlight({ children, tone = "light" }) {
  return (
    <span className={`relative inline-block ${tone === "dark" ? "text-[#d6ea4f]" : "text-[#1f8a4f]"}`}>
      {children}
      <svg
        className="absolute -left-[2%] w-[104%] -bottom-[8px] h-[10px] min-[576px]:-bottom-[10px] min-[576px]:h-[12px] pointer-events-none"
        viewBox="0 0 220 14"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M3 11 C 60 2, 150 1, 217 7"
          fill="none"
          stroke="#d6ea4f"
          strokeWidth="5"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

const STEPS = [
  {
    title: "Choose a plan",
    text: "Compare benefits and pick the plan that fits your family.",
    icon: ClipboardList,
  },
  {
    title: "Pay securely online",
    text: "Complete checkout with your preferred payment method.",
    icon: CreditCard,
  },
  {
    title: "Log in to the app",
    text: "See your plan, members and benefit credits in one place.",
    icon: Smartphone,
  },
  {
    title: "Book & use benefits",
    icon: CalendarCheck,
    text: "Consult a doctor, book tests or order medicines with your plan.",
  },
];

function useInView2(threshold = 0.25) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const motionSafe =
  "motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0";

function HowItWorks({ steps = STEPS }) {
  const [headRef, headIn] = useInView2(0.3);
  const [stepsRef, stepsIn] = useInView2(0.25);

  return (
    <section
      aria-labelledby="how-title"
      className="bg-[#f6f5f0] py-[30px] flex flex-col items-center justify-center gap-12 lg:flex-row lg:gap-0 min-[992px]:py-[100px] text-[#0d2f27]"
    >
      <div className="mx-auto w-full max-w-[1000px] px-[18px] sm:px-8 lg:w-auto">
        <div
          ref={headRef}
          className={`transition duration-700 ease-out ${motionSafe} ${headIn ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
        >
          <Eyebrow>How it works</Eyebrow>
          <h2
            id="how-title"
            className="mt-[20px] mb-[0px] text-[34px] sm:text-[44px] xl:text-[48px] font-extrabold leading-[1.08] tracking-[-0.03em] text-[#0d2f27]"
          >
            From plan to doctor in <Highlight>four steps.</Highlight>
          </h2>
        </div>

        <div ref={stepsRef} className="relative mt-16">
          {/* Connecting line: centre of circle 1 → centre of circle 4 (desktop only) */}
          <div
            aria-hidden="true"
            className="absolute left-7 right-[calc((100%_-_84px)/4_-_28px)] top-7 hidden h-0.5 overflow-hidden bg-[#DCD7CA] lg:block"
          >
            <span
              className={`block h-full origin-left bg-[#0d2f27] transition-transform delay-200 duration-[1400ms] ease-out motion-reduce:scale-x-100 motion-reduce:transition-none ${
                stepsIn ? "scale-x-100" : "scale-x-0"
              }`}
            />
          </div>

          <ol className="relative m-0 grid list-none grid-cols-1 gap-10 p-0 sm:grid-cols-2 lg:grid-cols-4 lg:gap-7">
            {steps.map(({ icon: StepIcon, ...s }, i) => (
              <li
                key={s.title}
                style={{ transitionDelay: stepsIn ? `${i * 150}ms` : "0ms" }}
                className={`group transition duration-700 ease-out ${motionSafe} ${stepsIn ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}
              >
                <div
                  aria-hidden="true"
                  className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-solid border-[#0d2f27] bg-[#f6f5f0] text-lg font-extrabold text-[#0d2f27] transition duration-300 group-hover:scale-110 group-hover:bg-[#0d2f27] group-hover:text-[#d6ea4f] motion-reduce:transition-none"
                >
                  <StepIcon size={24} strokeWidth={1.8} />
                </div>
                <h3 className="mt-6 mb-[0px] text-[22px] font-extrabold leading-[1.2] tracking-[-0.02em] text-[#0d2f27]">
                  <span className="sr-only">Step {i + 1}: </span>
                  {s.title}
                </h3>
                <p className="mt-2.5 mb-[0px] text-[15px] leading-[1.6] text-[#5b6b66]">
                  {s.text}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
      <div className="flex w-full flex-col items-center gap-6 px-[18px] sm:w-auto sm:flex-row sm:gap-4 sm:px-8 lg:gap-0 lg:px-0">
        <div className="relative  flex flex-col w-full max-w-[280px] items-center gap-2 rounded-2xl border border-[#0d2f27]/10 bg-white  shadow-[0_12px_40px_rgba(13,47,39,0.08)] p-3 lg:-mt-2">
          <div className="min-w-0 flex-1">
            <h3 className="mb-0 mt-2 text-lg font-extrabold leading-snug tracking-[-0.03em] text-[#0d2f27]">
              {" "}
              Healthcare, in your pocket.
            </h3>

            <p className="mb-0 mt-1 text-sm leading-5 text-[#64736d]">
              Scan the QR code to download the app and access your benefits.
            </p>

            <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#176a40]">
              <span>Quick & easy access</span> <span aria-hidden="true">↗</span>
            </div>
          </div>

          {/* Keep QR crisp and large enough to scan */}
          <div className="flex shrink-0 flex-col items-center gap-2 rounded-xl border border-[#e8ece6] bg-[#fbfcfa] p-2.5 sm:p-3">
            <img
              src="/assets/img/qrcode.png"
              alt="QR code to download the app"
              width={120}
              height={120}
              loading="lazy"
              className="block h-[104px] w-[104px] object-contain sm:h-[120px] sm:w-[120px]"
            />
            <span className="text-[10px] font-semibold tracking-wide text-[#64736d] sm:text-xs">
              SCAN TO DOWNLOAD
            </span>
          </div>
        </div>
        <div>
          <img
            src="/assets/img/swasthopdMobile.png"
            alt="mobile view"
            width={300}
            height={590}
            className="block h-auto w-[220px] sm:w-[260px] lg:w-[300px]"
          />
        </div>
      </div>
    </section>
  );
}


function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Arrow({ className = "h-[18px] w-[18px]" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

const reveal = (on) =>
  `transition duration-700 ease-out motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0 ${
    on ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
  }`;

const brands = [
  { src: "assets/img/provider/aarthi-scans.png", alt: "Brand 1" },
  { src: "assets/img/provider/agilus-diagnostics.png", alt: "Brand 2" },
  { src: "assets/img/provider/apollo-hospital.png", alt: "Brand 3" },
  { src: "assets/img/provider/beato.png", alt: "Brand 4" },
  { src: "assets/img/provider/healthians.png", alt: "Brand 5" },
  { src: "assets/img/provider/manipalhospital.png", alt: "Brand 6" },
  { src: "assets/img/provider/orange-health.png", alt: "Brand 7" },
  { src: "assets/img/provider/pathkind-labs.png", alt: "Brand 8" },
  { src: "assets/img/provider/practo.png", alt: "Brand 9" },
  { src: "assets/img/provider/redcliff.png", alt: "Brand 10" },
  { src: "assets/img/provider/tata-1mg.png", alt: "Brand 11" },
  { src: "assets/img/provider/thryocare.png", alt: "Brand 12" },
  { src: "assets/img/provider/toothsi.png", alt: "Brand 13" },
  { src: "assets/img/provider/trexova.png", alt: "Brand 14" },
];

function BrandCarousel() {
  const loopBrands = [...brands, ...brands];

  return (
    <section className="w-full bg-white pt-[0px] pb-[64px] min-[992px]:pb-[100px] overflow-hidden">
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }


        @media (prefers-reduced-motion: reduce) {
          .animate-marquee { animation: none; }
        }
      `}</style>

      <div className="max-w-[1240px] mx-auto px-[18px] min-[576px]:px-8 flex flex-col xl:flex-row xl:items-center gap-10 xl:gap-16">
        {/* Title */}
        <div className="w-full xl:w-auto xl:shrink-0">
          <Eyebrow>Our partners</Eyebrow>
          <h2 className="mt-[20px] mb-[0px] text-[34px] sm:text-[40px] xl:text-[44px] font-extrabold leading-[1.1] tracking-[-0.03em] text-[#0d2f27]">
            Trusted brands <br className="hidden xl:inline" />
            <Highlight>work with us</Highlight>
          </h2>
        </div>

        {/* Marquee */}
        <div className="w-full min-w-0 xl:flex-1">
          <div className="relative w-full overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_8%,#000_92%,transparent)] [-webkit-mask-image:linear-gradient(90deg,transparent,#000_8%,#000_92%,transparent)]">
            <div className="flex w-max animate-marquee">
              {loopBrands.map((brand, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center shrink-0 px-8 md:px-12"
                >
                  <img
                    src={brand.src}
                    alt={brand.alt}
                    className="h-10 md:h-12 w-auto object-contain transition duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const heroStats = [
  { value: "₹5,000", suffix: "+ GST", label: "Plans from, for 4 members" },
  { value: "Up to 50%", label: "Off diagnostic tests" },
  { value: "Up to 20%", label: "Off prescribed medicines" },
];

function HeroSection() {
  return (
    <section className="p-[8px] min-[576px]:p-3 bg-[#f6f5f0]">
      <div className="relative overflow-hidden rounded-[20px] min-[576px]:rounded-[28px] bg-[#0d2f27] bg-hero-card bg-hero-grid text-white px-[18px] pt-8 min-[576px]:px-8 min-[576px]:pt-10 min-[992px]:flex min-[992px]:items-center min-[992px]:min-h-[500px] min-[992px]:px-6 min-[992px]:pt-[56px] min-[992px]:pb-[56px]">
        <div className="relative z-[2] w-full max-w-[1330px] mx-auto">
          {/* Copy */}
          <div className="max-w-[600px] min-[992px]:max-w-[500px] min-[1201px]:max-w-[560px]">
            <span className="inline-flex items-center gap-2.5 py-1.5 pl-3 pr-3.5 mb-[20px] min-[576px]:mb-[22px] border-[1px] border-solid border-white/[0.28] rounded-full text-xs min-[576px]:text-[13px] font-medium text-white">
              <span className="w-[7px] h-[7px] shrink-0 rounded-full bg-[#d6ea4f]" />
              OPD + insurance benefits in one family plan
            </span>

            <h1 className="mb-6 min-[576px]:mb-8 text-[34px] min-[361px]:text-[40px] min-[576px]:text-[46px] min-[1201px]:text-[52px] leading-[1.04] font-extrabold tracking-[-0.035em] text-white">
              Everyday <br className="hidden min-[576px]:inline" />
              healthcare,{" "}
              <span className="relative inline-block text-[#d6ea4f]">
                covered
                <svg
                  className="absolute -left-[2%] w-[104%] -bottom-[10px] h-[10px] min-[576px]:-bottom-[12px] min-[576px]:h-[12px] pointer-events-none"
                  viewBox="0 0 220 14"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 11 C 60 2, 150 1, 217 7"
                    fill="none"
                    stroke="#d6ea4f"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <br className="hidden min-[576px]:inline" /> for the whole family.
            </h1>

            <p className="max-w-[460px] mb-6 min-[576px]:mb-7 text-base min-[576px]:text-lg leading-normal text-white/[0.85]">
              Doctor consultations, diagnostics, medicines and wellness for your family
              members with accident and hospital-cash insurance included at no
              extra cost.
            </p>

            <div className="flex flex-col min-[576px]:flex-row min-[576px]:flex-wrap gap-[12px]">
              <a
                className="inline-flex items-center justify-center gap-2.5 w-full min-[576px]:w-auto px-[22px] py-[12px] rounded-md text-[15px] font-bold leading-[1.2] no-underline transition duration-200 bg-[#d6ea4f] text-[#0d2f27] shadow-[0_8px_28px_rgba(214,234,79,0.25)] hover:bg-[#e1f165] hover:text-[#0d2f27] focus:text-[#0d2f27] hover:-translate-y-px"
                href="/plans"
              >
                Explore family plans
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 12h14M13 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </div>

            <HealthcareBenefits/>

            
          </div>
        </div>

        {/* Photo */}
        <div className="relative z-[1] pointer-events-none h-[240px] -mx-[18px] mt-7 mask-fade-b after:content-[''] after:absolute after:inset-0 after:bg-hero-shade-b min-[576px]:h-[320px] min-[576px]:-mx-8 min-[576px]:mt-8 min-[992px]:absolute min-[992px]:inset-y-0 min-[992px]:right-0 min-[992px]:w-[46%] min-[992px]:h-auto min-[992px]:m-0 min-[992px]:mask-fade-r min-[992px]:after:bg-hero-shade min-[1201px]:w-[50%]">
          <picture className="block h-full">
            <source
              srcSet="/assets/img/hero/heroImg-crop.webp"
              type="image/webp"
            />
            <img
              className="block w-full h-full object-cover object-[55%_25%] min-[992px]:object-[60%_30%] animate-hero-reveal motion-reduce:animate-none"
              src="/assets/img/hero/heroImg-crop.jpg"
              alt="Smiling family of three sitting together at home"
              width="1189"
              height="942"
              fetchPriority="high"
            />
          </picture>
        </div>
      </div>
    </section>
  );
}

// Each card is tinted to match the palette of its illustration
const SERVICE_CARDS = [
  {
    title: "Lab Tests",
    text: "Accurate testing & diagnostics for your healthcare needs",
    img: "/assets/img/hero/Lab.jpg",
    alt: "Lab technician collecting a sample from a patient",
    card: "from-[#eef8d9] border-[#dde7b4]",
    accent: "bg-[#8f9b1f]",
  },
  {
    title: "Doctor Consultation",
    text: "Connect with doctors for trusted medical guidance",
    img: "/assets/img/hero/Inclinic.png",
    alt: "Doctor checking a patient's blood pressure in a clinic",
    card: "from-[#fdeee1] border-[#f1d5bc]",
    accent: "bg-[#e8873a]",
  },
  {
    title: "Virtual Consultation",
    text: "See a doctor over video without leaving home",
    img: "/assets/img/hero/Video.png",
    alt: "Patient on a video call with a doctor",
    card: "from-[#f1eafb] border-[#ddd0f0]",
    accent: "bg-[#7c5cbf]",
  },
  {
    title: "Pharmacy",
    text: "Save up to 20% on your prescribed medicines",
    img: "/assets/img/hero/Pharmacy.jpg",
    alt: "Medicines being delivered to a customer's door",
    card: "from-[#e2f4ea] border-[#c4e5d3]",
    accent: "bg-[#2f9e6e]",
  },
  {
    title: "Tele Consultation",
    text: "Unlimited doctor calls whenever you need advice",
    img: "/assets/img/hero/Tele.jpg",
    alt: "Patient talking to a doctor on a phone call",
    card: "from-[#e5f0fb] border-[#c9ddf2]",
    accent: "bg-[#3b82c4]",
  },
];

function ServiceCarousel() {
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  return (
    <section
      aria-label="Our services"
      className="bg-[#f6f5f0] px-[8px] pt-[8px] pb-[24px] min-[576px]:px-3 min-[576px]:pt-3 min-[992px]:pb-[40px]"
    >
      <SwiperReact
        modules={[Autoplay]}
        loop={true}
        speed={reduceMotion ? 300 : 6000}
        autoplay={
          reduceMotion
            ? false
            : { delay: 0, disableOnInteraction: false, pauseOnMouseEnter: true }
        }
        slidesPerView={1.12}
        spaceBetween={12}
        breakpoints={{
          576: { slidesPerView: 1.6, spaceBetween: 16 },
          992: { slidesPerView: 2.2, spaceBetween: 20 },
          1201: { slidesPerView: 2.6, spaceBetween: 24 },
          1440: { slidesPerView: 3.1, spaceBetween: 24 },
          1760: { slidesPerView: 3.6, spaceBetween: 24 },
        }}
        style={{ "--swiper-wrapper-transition-timing-function": "linear" }}
        className="!overflow-hidden rounded-[20px]"
      >
        {/* Cards repeated so the loop always has enough slides to stay seamless */}
        {[...SERVICE_CARDS, ...SERVICE_CARDS].map((s, i) => (
          <SwiperSlide key={i} className="!h-auto">
            <div
              aria-hidden={i >= SERVICE_CARDS.length || undefined}
              tabIndex={i >= SERVICE_CARDS.length ? -1 : undefined}
              className={`group flex h-full items-center gap-3 min-[576px]:gap-4 rounded-[16px] border-[1px] border-solid bg-gradient-to-br to-white px-3 py-3 min-[576px]:px-5 min-[576px]:py-4 no-underline transition duration-300 hover:shadow-[0_16px_36px_-24px_rgba(13,47,39,0.45)] ${s.card}`}
            >
              <img
                src={s.img}
                alt={s.alt}
                width="1344"
                height="768"
                loading="lazy"
                className="h-[78px] w-[92px] shrink-0 object-cover mix-blend-multiply min-[576px]:h-[104px] min-[576px]:w-[132px]"
              />
              <div className="min-w-0 flex-1">
                <h3 className="mb-[4px] text-[17px] min-[576px]:text-[21px] font-extrabold leading-[1.2] tracking-[-0.02em] text-[#0d2f27]">
                  {s.title}
                </h3>
                <p className="mb-[0px] text-[13px] min-[576px]:text-[15px] leading-[1.5] text-[#3d4b46]">
                  {s.text}
                </p>
              </div>
              {/* <span
                aria-hidden="true"
                className={`flex h-7 w-7 min-[576px]:h-8 min-[576px]:w-8 shrink-0 items-center justify-center rounded-full text-white transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none ${s.accent}`}
              >
                <ChevronRight size={18} strokeWidth={2.6} />
              </span> */}
            </div>
          </SwiperSlide>
        ))}
      </SwiperReact>
    </section>
  );
}

const testimonials = [
  {
    quote:
      "The customer support is exceptional, and the platform is so easy to use. I feel confident knowing my health is in good hands.",
    name: "Suresh Iyer",
    city: "Chennai",
  },
  {
    quote:
      "This platform has been a game changer for me and my parents. The affordability and accessibility of their plans are unparalleled.",
    name: "Priya Das",
    city: "Kolkata",
  },
  {
    quote:
      "I'm impressed by their dedication to making healthcare affordable. Their OPD plans fit perfectly within my budget, without compromising quality.",
    name: "Karan Singh",
    city: "Chandigarh",
  },
  {
    quote:
      "I travel a lot for work, and Swasth OPD plans have given me peace of mind no matter where I am. Truly reliable and convenient.",
    name: "Vikram Mehta",
    city: "Bangalore",
  },
  {
    quote:
      "Their OPD coverage is a lifesaver. I've saved so much on regular doctor visits and diagnostic tests. Thank you for this amazing service!",
    name: "Sneha Gupta",
    city: "Jaipur",
  },
  {
    quote:
      "From consultations to diagnostics, everything is hassle-free. It's so convenient, especially when you have kids at home.",
    name: "Pooja Verma",
    city: "Delhi",
  },
];

function QuoteMark({ className }) {
  return (
    <svg
      className={className}
      viewBox="-2 -2 68 56"
      fill="currentColor"
      stroke="#0d2f27"
      strokeWidth="2.5"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 52a14 14 0 1 1 0-28c1 0 2 .1 2.9.3C14.6 15.6 9.8 9.5 2 6.4L5 0c14.7 5 23 17.2 23 32.6V38a14 14 0 0 1-14 14z" />
      <path
        transform="translate(34 0)"
        d="M14 52a14 14 0 1 1 0-28c1 0 2 .1 2.9.3C14.6 15.6 9.8 9.5 2 6.4L5 0c14.7 5 23 17.2 23 32.6V38a14 14 0 0 1-14 14z"
      />
    </svg>
  );
}

const networkStats = [
  {
    value: 600,
    suffix: "+",
    label: "Doctors",
    description: "Doctors across specialities, available through your plan.",
    icon: "fas fa-user-md",
    lottie: "/assets/lottie/stat-doctor.json",
  },
  {
    value: 220,
    suffix: "+",
    label: "Hospitals",
    description: "Partner hospitals and clinics for consultations and care.",
    icon: "fas fa-hospital-alt",
    lottie: "/assets/lottie/stat-hospital.json",
  },
  {
    value: 110,
    suffix: "+",
    label: "Corporates & Associations",
    description: "Organisations that offer Swasth OPD to their people.",
    icon: "fas fa-handshake",
    lottie: "/assets/lottie/stat-network.json",
  },
  {
    value: 28,
    suffix: "K",
    label: "Customers",
    description: "Members who rely on Swasth OPD for everyday healthcare.",
    icon: "fas fa-users",
    lottie: "/assets/lottie/stat-customers.json",
    featured: true,
  },
];

// Counts up from 0 the first time the number scrolls into view
function CountUp({ end, duration = 1800 }) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(end);
      return undefined;
    }

    let frame;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          setValue(Math.round(end * (1 - Math.pow(1 - progress, 3))));
          if (progress < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [end, duration]);

  return <span ref={ref}>{value}</span>;
}

const aboutHighlights = [
  "Affordable OPD for consultations and tests.",
  "Cashless OPD benefits for seamless access.",
  "Mental health, fitness, and lifestyle programs.",
  "Employee wellness solutions for businesses.",
  "Access to top doctors and diagnostic centers.",
];

const HomePage = () => {
  useEffect(() => {
    const swiper = new Swiper(".testimonial-style-two-carousel", {
      slidesPerView: 1,
      spaceBetween: 30,
      loop: true,
      autoplay: {
        delay: 3500,
        disableOnInteraction: false,
      },
      pagination: {
        el: ".testimonial-pagination",
        clickable: true,
      },
      breakpoints: {
        992: { slidesPerView: 2, spaceBetween: 30 },
      },
    });
    return () => {
      swiper.destroy(true, true);
    };
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <HeroSection />
      <ServiceCarousel />
      {/* Start About */}
      <section className="relative overflow-hidden bg-white py-[64px] min-[992px]:py-[100px]">
        <div className="max-w-[1320px] mx-auto px-[18px] min-[576px]:px-8 grid gap-12 min-[992px]:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] min-[992px]:gap-12 min-[1201px]:gap-16">
          {/* Copy */}
          <div>
            <Eyebrow>About us</Eyebrow>
            <h2 className="mt-[20px] mb-[24px] text-[34px] min-[576px]:text-[44px] min-[1201px]:text-[48px] font-extrabold leading-[1.1] tracking-[-0.03em] text-[#0d2f27]">
              Swasth OPD: <br className="hidden min-[576px]:inline" />
              Your Partner in <br className="hidden min-[576px]:inline" />
              <Highlight>Health and Wellness</Highlight>
            </h2>
            <p className="max-w-[560px] mb-[0px] text-[16px] min-[576px]:text-[18px] leading-[1.6] text-[#5b6b66]">
              At Swasth OPD, we are committed to revolutionizing healthcare by
              bridging the gap between wellness and accessibility. As a leading
              health and wellness broker, we specialize in offering tailor-made
              health plans that cater to businesses and individuals alike.
            </p>

            <div className="mt-10 grid gap-8 min-[576px]:grid-cols-[auto_minmax(0,1fr)] min-[576px]:gap-0">
              {/* Experience */}
              <div className="min-[576px]:pr-10">
                <svg
                  className="w-[48px] h-[40px] text-[#1f6b45]"
                  viewBox="0 0 48 40"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <circle cx="24" cy="9" r="6" />
                  <circle cx="9" cy="13" r="4.5" />
                  <circle cx="39" cy="13" r="4.5" />
                  <path
                    d="M13 37v-4a11 11 0 0 1 22 0v4z"
                    strokeLinejoin="round"
                  />
                  <path d="M2 35v-2.5A7.5 7.5 0 0 1 9.5 25H11" />
                  <path d="M46 35v-2.5A7.5 7.5 0 0 0 38.5 25H37" />
                </svg>
                <div className="mt-[14px] text-[64px] min-[576px]:text-[76px] font-extrabold leading-none tracking-[-0.04em] text-[#0d2f27]">
                  <CountUp end={12} />
                  <span className="text-[#1f8a4f]">+</span>
                </div>
                <p className="mt-[12px] mb-[0px] text-[14px] min-[576px]:text-[15px] font-bold uppercase tracking-[0.08em] text-[#0d2f27]">
                  Years of experience
                </p>
              </div>

              {/* Highlights */}
              <ul className="list-none my-[0px] pl-[0px] flex flex-col gap-[16px] pt-8 border-t-[1px] border-solid border-[#dfe5e2] min-[576px]:pt-[4px] min-[576px]:border-t-0 min-[576px]:border-l-[1px] min-[576px]:pl-10">
                {aboutHighlights.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-[14px] text-[15px] min-[576px]:text-[16px] leading-[1.5] font-medium text-[#0d2f27]"
                  >
                    <span className="flex items-center justify-center shrink-0 w-[30px] h-[30px] -mt-[3px] rounded-full bg-[#e3f0e6] text-[#135e3f]">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M5 12.5l4.5 4.5L19 7.5" />
                      </svg>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Photo */}
          <div className="relative">
            <img
              src="/assets/img/bnr3.jpg"
              alt="Smiling family of four at home"
              width="638"
              height="887"
              loading="lazy"
              className="block w-full aspect-[4/5] min-[576px]:aspect-[666/695] min-[992px]:absolute min-[992px]:inset-x-0 min-[992px]:top-[30px] min-[992px]:bottom-0 min-[992px]:h-[calc(100%-30px)] min-[992px]:aspect-auto object-cover object-[50%_30%] rounded-[16px] shadow-[0_30px_60px_-30px_rgba(13,47,39,0.35)]"
            />
            {/* <svg
              className="absolute -right-[14px] min-[576px]:-right-[36px] top-[44%] w-[84px] min-[576px]:w-[120px] h-auto text-[#8fc3a0] opacity-80 pointer-events-none"
              viewBox="0 0 120 140"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M56 138c-2-30 2-58 16-82" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              <path d="M70 60c-4-26 10-46 44-56 4 30-12 50-44 56z" />
              <path d="M60 96c4-22 22-34 50-32-6 24-24 34-50 32z" />
              <path d="M58 82C40 70 20 72 6 86c18 14 38 12 52-4z" opacity="0.75" />
            </svg> */}
          </div>
        </div>
      </section>

      <BrandCarousel />
      <HowItWorks />
      <Services />

      {/* Start Fun Factor */}
      <section className="relative overflow-hidden bg-[#f6f5f0] py-[64px] min-[992px]:py-[100px]">
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[55%] bg-[#e3efe0] [clip-path:ellipse(80%_100%_at_50%_100%)]" />
        <div className="relative max-w-[1240px] mx-auto px-[18px] min-[576px]:px-8">
          <div className="max-w-[640px]">
            <Eyebrow>Our network</Eyebrow>
            <h2 className="mt-[20px] mb-[0px] text-[34px] sm:text-[44px] xl:text-[48px] font-extrabold leading-[1.08] tracking-[-0.03em] text-[#0d2f27]">
              A care network that <Highlight>keeps growing</Highlight>
            </h2>
          </div>

          <div className="mt-12 grid gap-6 items-stretch min-[576px]:grid-cols-2 min-[1201px]:grid-cols-4">
            {networkStats.map((stat) => (
              <div
                key={stat.label}
                className="group relative isolate flex flex-col h-full overflow-hidden rounded-[28px] p-6 min-[576px]:p-7 bg-[#fff] border-[1px] border-solid border-[#0d2f27]/[0.06] shadow-[0_24px_48px_-28px_rgba(13,47,39,0.35)] transition duration-300 ease-out hover:-translate-y-1.5 hover:border-[#0d2f27] hover:shadow-[0_32px_60px_-28px_rgba(13,47,39,0.6)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                {/* Dark hero background fades in on hover */}
                <span
                  aria-hidden="true"
                  className="absolute inset-0 -z-10 bg-[#0b2a23] bg-hero-card bg-hero-grid opacity-[0] transition-opacity duration-300 group-hover:opacity-[1]"
                />
                <svg
                  className="absolute top-[26px] right-[26px] w-[44px] h-[44px] text-[#d6ea4f]/50 opacity-[0] -rotate-12 scale-90 transition duration-300 group-hover:opacity-[1] group-hover:rotate-0 group-hover:scale-100"
                  viewBox="0 0 48 48"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M44 4C28 4 16 12 14 26c-.4 3 .2 6 1.4 8.6C24 33 34 26 44 4z" />
                  <path d="M4 24c10 0 17 5 18 13 .2 2-.1 4-.9 5.6C14 41 8 37 4 24z" opacity="0.7" />
                </svg>

                <span className="flex items-center justify-center w-[52px] h-[52px] rounded-full text-[20px] bg-[#e8f3ec] text-[#1f6b45] transition-colors duration-300 group-hover:bg-white/10 group-hover:text-[#d6ea4f]">
                  <i className={stat.icon} aria-hidden="true" />
                </span>

                <div className="mt-6 text-[44px] font-extrabold leading-none tracking-[-0.03em] text-[#0d2f27] transition-colors duration-300 group-hover:text-white">
                  <CountUp end={stat.value} />
                  <span className="text-[#1f8a4f] transition-colors duration-300 group-hover:text-[#d6ea4f]">
                    {stat.suffix}
                  </span>
                </div>

                <h3 className="mt-[8px] mb-[0px] text-[22px] font-extrabold leading-[1.2] tracking-[-0.02em] text-[#0d2f27] transition-colors duration-300 group-hover:text-white">
                  {stat.label}
                </h3>

                <p className="flex-1 mt-[12px] mb-[0px] text-[15px] leading-[1.55] text-[#5b6b66] transition-colors duration-300 group-hover:text-white/75">
                  {stat.description}
                </p>

                <div className="mt-6 flex items-center justify-center aspect-[4/3] rounded-[22px] bg-[#eef6f0] overflow-hidden transition duration-300 group-hover:ring-4 group-hover:ring-white/10">
                  <DotLottieReact
                    src={stat.lottie}
                    loop
                    autoplay
                    className="w-[78%] h-[78%] transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Start Testimonial */}
      <section className="relative overflow-hidden bg-[#0a7637] bg-stars bg-[length:160px_160px] py-[64px] min-[992px]:py-[100px]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_70%_at_85%_50%,rgba(214,234,79,0.12),transparent_70%)]" />
        <div className="relative max-w-[1180px] mx-auto px-[18px] min-[576px]:px-8 grid gap-10 min-[992px]:grid-cols-[minmax(0,360px)_minmax(0,1fr)] min-[992px]:gap-14 min-[992px]:items-center">
          {/* Heading */}
          <div>
            <span className="inline-flex items-center gap-2.5 py-1.5 pl-3 pr-3.5 mb-[20px] border-[1px] border-solid border-white/[0.28] rounded-full text-[13px] font-medium text-white">
              <span className="w-[7px] h-[7px] shrink-0 rounded-full bg-[#d6ea4f]" />
              What our members say
            </span>
            <h2 className="mb-[20px] text-[34px] min-[576px]:text-[44px] leading-[1.08] font-extrabold tracking-[-0.03em] text-white">
              Over <span className="text-[#d6ea4f]">100+</span> businesses &amp;{" "}
              <span className="text-[#d6ea4f]">28,000</span> customers across
              India
            </h2>
            <p className="mb-[0px] max-w-[340px] text-[16px] leading-[1.6] text-white/70">
              Families and teams trust Swasth OPD for everyday doctor visits,
              diagnostics and medicines.
            </p>
            <div className="testimonial-pagination mt-8 flex gap-[8px] [&_.swiper-pagination-bullet]:m-0 [&_.swiper-pagination-bullet]:w-[8px] [&_.swiper-pagination-bullet]:h-[8px] [&_.swiper-pagination-bullet]:rounded-full [&_.swiper-pagination-bullet]:bg-white/30 [&_.swiper-pagination-bullet]:opacity-100 [&_.swiper-pagination-bullet]:transition-all [&_.swiper-pagination-bullet-active]:!w-[28px] [&_.swiper-pagination-bullet-active]:!bg-[#d6ea4f]" />
          </div>

          {/* Cards */}
          <div className="testimonial-style-two-carousel swiper w-full">
            <div className="swiper-wrapper">
              {testimonials.map((item) => (
                <div
                  key={item.name}
                  className="swiper-slide !h-auto pt-[34px] pb-[34px]"
                >
                  <figure className="relative h-full m-[0px] flex flex-col rounded-[22px] bg-white px-7 pt-12 pb-10 min-[576px]:px-9 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.45)]">
                    <QuoteMark className="absolute -top-[30px] left-[22px] w-[64px] h-[52px] text-[#0f794d]" />
                    <QuoteMark className="absolute -bottom-[30px] right-[22px] w-[64px] h-[52px] rotate-180 text-[#0f794d]" />

                    <div
                      className="absolute top-[22px] right-[24px] flex gap-[3px] text-[#e7b416]"
                      aria-label="Rated 5 out of 5"
                    >
                      {Array.from({ length: 5 }).map((_, i) => (
                        <svg
                          key={i}
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8L12 2.5z" />
                        </svg>
                      ))}
                    </div>

                    <blockquote className="m-[0px] flex-1 text-[17px] min-[576px]:text-[19px] font-extrabold uppercase leading-[1.3] tracking-[0.005em] text-[#0d2f27]">
                      {item.quote}
                    </blockquote>
                    <figcaption className="mt-6 text-[14px] font-bold uppercase tracking-[0.06em] text-[#1f8a4f]">
                      — {item.name}
                      <span className="ml-[6px] font-semibold normal-case tracking-normal text-[#5b6b66]">
                        {item.city}
                      </span>
                    </figcaption>
                  </figure>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
